import { ChevronLeftIcon, ChevronRightIcon, DoubleArrowLeftIcon, DoubleArrowRightIcon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@registry/new-york/ui/select';
import { InventoryCounterStore } from '@stores';
import { Table } from '@tanstack/react-table';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';

import styles from './DataTablePagination.module.scss';

interface DataTablePaginationStoreProps<TData> {
  table: Table<TData>;
  store: InventoryCounterStore;
  pagesSelect?: number[];
  hideRowsPerPage?: boolean;
}

export const DataTablePaginationStore = observer(
  <TData,>({ table, store, pagesSelect, hideRowsPerPage = false }: DataTablePaginationStoreProps<TData>) => {
    const { count, counter, previousPage, nextPage, currentPage, setCurrentPage, setCounter, setLoader } = store;
    const numberOfPages = count === 0 ? Math.ceil(1 / counter) : Math.ceil(count / counter);
    const [pageInputValue, setPageInputValue] = useState<string>(currentPage.toString());

    useEffect(() => {
      setPageInputValue(currentPage.toString());
    }, [currentPage]);

    const applyPageInput = (raw: string) => {
      const rawValue = raw.trim();
      if (!rawValue) {
        setPageInputValue(currentPage.toString());
        return;
      }

      const value = Number(rawValue);

      if (!Number.isFinite(value) || value < 1 || value > numberOfPages) {
        // Invalid input: revert to the latest valid page (current table page)
        setPageInputValue(currentPage.toString());
        return;
      }

      setCurrentPage(value);
      setPageInputValue(value.toString());
    };

    const handlePageInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      applyPageInput(e.target.value);
    };

    const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Disallow decimal separators
      if ((e.key === '.' || e.key === ',') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        return;
      }
      if (e.key !== 'Enter') return;
      e.preventDefault();
      applyPageInput(e.currentTarget.value);
    };

    return (
      <div className={styles.wrapper}>
        <div />
        <div className="flex items-center space-x-6 lg:space-x-8">
          {!hideRowsPerPage && (
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={counter.toString()}
                onValueChange={value => {
                  setCounter(Number(value));
                  setCurrentPage(1);
                  setLoader(true);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={table.getState().pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {(pagesSelect || [10, 20, 30, 40]).map(pageSize => (
                    <SelectItem
                      key={pageSize}
                      value={`${pageSize}`}
                    >
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-center text-sm font-medium">
            Page
            <input
              className={styles.pageInput}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pageInputValue}
              onChange={e => setPageInputValue(e.target.value.replace(/[^\d]/g, ''))}
              onBlur={handlePageInputBlur}
              onKeyDown={handlePageInputKeyDown}
            />
            of {numberOfPages}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => {
                setCurrentPage(1);
                setLoader(true);
              }}
              disabled={!previousPage}
            >
              <span className="sr-only">Go to first page</span>
              <DoubleArrowLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                setCurrentPage(previousPage);
                setLoader(true);
              }}
              disabled={!previousPage}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                setCurrentPage(nextPage);
                setLoader(true);
              }}
              disabled={!nextPage}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => {
                setCurrentPage(numberOfPages);
                setLoader(true);
              }}
              disabled={!nextPage}
            >
              <span className="sr-only">Go to last page</span>
              <DoubleArrowRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  },
);
